import { useEffect, useRef, useState } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { searchProducts, formatINR, resolveMediaUrl } from "../lib/api";

export default function SearchDropdown({ open, onClose }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
    else {
      setQ("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      setBusy(false);
      return;
    }
    setBusy(true);
    const t = setTimeout(() => {
      searchProducts(q.trim())
        .then((d) => setResults(d.results || []))
        .catch(() => setResults([]))
        .finally(() => setBusy(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    onClose?.();
  };

  const goResult = (id) => {
    navigate(`/product/${id}`);
    onClose?.();
  };

  if (!open) return null;

  const highlight = (text) => {
    if (!q || !text) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx < 0) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark style={{ background: "rgba(245,213,101,0.35)", color: "#F5D565", padding: 0 }}>
          {text.slice(idx, idx + q.length)}
        </mark>
        {text.slice(idx + q.length)}
      </>
    );
  };

  return (
    <div
      data-testid="search-dropdown"
      className="fixed inset-0 z-[70]"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
      <div
        className="relative max-w-[720px] mx-auto mt-24 bg-[#0A0A0A] border border-[#262626] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={submit} className="flex items-center gap-3 border-b border-[#262626] pb-3">
          <Search size={18} strokeWidth={1.5} className="text-[#F5D565]" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search pieces, categories, tags…"
            data-testid="search-input"
            className="flex-1 bg-transparent outline-none text-[#F5F5F5] font-body text-base"
            style={{ letterSpacing: "0.01em" }}
          />
          {busy && <Loader2 size={16} className="animate-spin text-[#F5D565]" />}
          <button type="button" onClick={onClose} data-testid="search-close" className="text-[#A3A3A3] hover:text-[#F5D565]">
            <X size={18} />
          </button>
        </form>

        {/* Results */}
        <div className="mt-4 max-h-[60vh] overflow-y-auto" data-testid="search-results">
          {q.trim().length >= 2 && !busy && results.length === 0 && (
            <div className="py-10 text-center">
              <div className="font-serif-display italic" style={{ fontSize: 20, color: "rgba(255,255,255,0.7)" }}>
                No pieces match &ldquo;{q}&rdquo;
              </div>
              <p className="mt-2 font-body text-[13px]" style={{ color: "rgba(255,255,255,0.5)" }}>
                Try a different keyword — a category, collection, or colour.
              </p>
              <div className="mt-5 flex justify-center gap-2 flex-wrap">
                {["Earrings", "Necklaces", "Bracelets", "Daily Luxe", "Party Edit"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setQ(s)}
                    data-testid={`search-suggest-${s}`}
                    className="pill text-xs"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => goResult(p.id)}
              data-testid={`search-result-${p.id}`}
              className="w-full flex items-center gap-3 px-2 py-2.5 hover:bg-[#141414] text-left transition-colors"
            >
              <img
                src={resolveMediaUrl(p.images?.[0])}
                alt=""
                className="w-12 h-14 object-cover bg-[#141414]"
              />
              <div className="flex-1 min-w-0">
                <div className="font-serif-display truncate" style={{ fontSize: 15, color: "#FFFFFF" }}>
                  {highlight(p.name)}
                </div>
                <div className="font-ui mt-0.5" style={{ fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", color: "#A3A3A3" }}>
                  {p.category}
                </div>
              </div>
              <div className="font-body text-sm text-[#F5D565] whitespace-nowrap">
                {formatINR(p.price)}
              </div>
            </button>
          ))}
        </div>

        {q.trim().length >= 2 && results.length > 0 && (
          <button
            type="button"
            onClick={submit}
            data-testid="search-see-all"
            className="mt-4 w-full text-center font-ui py-2.5 border border-[#262626] hover:border-[#F5D565] hover:text-[#F5D565] transition-colors"
            style={{ fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)" }}
          >
            See all results
          </button>
        )}
      </div>
    </div>
  );
}
